import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ACCEPT_BOOKING_POLICIES, POLICY_DOCUMENTS } from '../services/policyService/queries';
import {
  isPolicyVersionOutdated,
  outdatedPolicyMessage,
  rentalAgreementOf,
  versionIdsFor,
  type CheckoutProduct,
  type PolicyDocumentRow,
} from '../services/policyService/checkoutPolicies';

/**
 * Spec 031 checkout: the version ids on screen, and one acceptance per booking.
 * A card error re-renders this page; the acceptance is not sent again.
 */
export function useCheckoutPolicies(product: CheckoutProduct) {
  const { data, loading, error, refetch } = useQuery(POLICY_DOCUMENTS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const [acceptPolicies] = useMutation(ACCEPT_BOOKING_POLICIES);
  const sent = useRef(new Set<string>());
  const [outdatedMessage, setOutdatedMessage] = useState<string | null>(null);

  const documents = useMemo(
    () => (data?.policyDocuments ?? []) as PolicyDocumentRow[],
    [data?.policyDocuments],
  );
  const versionIds = useMemo(() => versionIdsFor(product, documents), [product, documents]);
  const rental = useMemo(() => rentalAgreementOf(documents), [documents]);

  const accept = useCallback(
    async (bookingId: string) => {
      if (sent.current.has(bookingId)) return;
      let ids = versionIds;
      if (!ids.length) {
        const fresh = await refetch();
        ids = versionIdsFor(product, (fresh.data?.policyDocuments ?? []) as PolicyDocumentRow[]);
      }
      // Nothing is published yet (import has not run). The server still records a
      // missing acceptance in log mode; an empty id list is rejected by validation.
      if (!ids.length) return;
      try {
        await acceptPolicies({
          variables: { bookingId, input: { versionIds: ids, platform: 'web' } },
        });
        sent.current.add(bookingId);
      } catch (caught) {
        if (isPolicyVersionOutdated(caught)) {
          setOutdatedMessage(outdatedPolicyMessage(caught));
          await refetch();
        }
        throw caught;
      }
    },
    [acceptPolicies, product, refetch, versionIds],
  );

  return {
    versionIds,
    rental,
    loading,
    error,
    outdatedMessage,
    clearOutdated: () => setOutdatedMessage(null),
    accept,
  };
}
